/**
 * Verify-email landing page. Loaded when the URL is /verify-email?token=...
 * (the backend's email magic link points here). Hits the verify endpoint
 * and shows success / failure copy. No React Router required — the App
 * component checks pathname on mount and renders this when matched.
 */
import { useEffect, useState } from 'react';

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; email: string }
  | { kind: 'error'; message: string };

interface VerifyEmailProps {
  /** Called after success/failure so the parent can return to the main app. */
  onReturn: () => void;
}

export function VerifyEmail({ onReturn }: VerifyEmailProps) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setState({ kind: 'error', message: 'No verification token in the URL.' });
      return;
    }
    fetch('/api/auth/verify-email?token=' + encodeURIComponent(token), {
      credentials: 'same-origin',
    })
      .then(async (res) => {
        const text = await res.text();
        const json = text ? JSON.parse(text) : null;
        if (!res.ok) {
          throw new Error(json?.error?.message || `Failed (${res.status})`);
        }
        setState({ kind: 'ok', email: json?.user?.email || '' });
      })
      .catch((err) => {
        setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      });
  }, []);

  return (
    <div className="verify">
      <div className="verify__card">
        <h1 className="verify__title">Email verification</h1>
        {state.kind === 'loading' && <p>Checking your link…</p>}
        {state.kind === 'ok' && (
          <>
            <p className="verify__success">
              ✓ Email verified{state.email ? ` for ${state.email}` : ''}.
            </p>
            <p>You're all set. Free tier gives you 10 generations/day.</p>
          </>
        )}
        {state.kind === 'error' && (
          <>
            <p className="verify__error">{state.message}</p>
            <p>
              Verification links expire after 7 days. Sign in and request a new
              one if needed.
            </p>
          </>
        )}
        <button
          type="button"
          className="button button--primary button--large"
          onClick={onReturn}
        >
          Continue to Lites
        </button>
      </div>
    </div>
  );
}
